import styled from 'styled-components'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useNavigate } from 'react-router-dom'
import useLocale from '../../hooks/useLocale'

const Card = styled.div`
    background: ${({ theme }) => theme.card.background};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.5rem;
    padding: 0.75rem;
    cursor: ${({ $isDragging }) => $isDragging ? 'grabbing' : 'grab'};
    opacity: ${({ $isDragging }) => $isDragging ? 0.4 : 1};
    box-shadow: ${({ $isDragging }) => $isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.06)'};
    transition: box-shadow 0.15s ease;
    user-select: none;

    &:hover {
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    }

    @media (max-width: ${({ theme }) => theme.breakpoint.s}) {
        padding: 0.4rem 0.5rem;
    }
`

const CardHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
`

const CardTitle = styled.p`
    margin: 0;
    font-size: var(--font-XS);
    font-weight: 600;
    color: ${({ theme }) => theme.font.color};
    line-height: 1.3;
    flex: 1;
    min-width: 0;
`

const DueDate = styled.span`
    font-size: var(--font-XS);
    color: ${({ $overdue }) => $overdue ? 'var(--clr-dark-red)' : 'inherit'};
    opacity: ${({ $overdue }) => $overdue ? 1 : 0.6};
    white-space: nowrap;
    flex-shrink: 0;

    @media (max-width: ${({ theme }) => theme.breakpoint.s}) {
        display: none;
    }
`

const CardMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    align-items: center;
`

const PriorityBadge = styled.span`
    font-size: var(--font-XS);
    padding: 0.1rem 0.45rem;
    border-radius: 0.75rem;
    font-weight: 500;
    background: ${({ $priority }) =>
        $priority === 'high'   ? 'rgba(170,17,0,0.12)'  :
        $priority === 'medium' ? 'rgba(227,177,26,0.15)' :
                                 'rgba(8,146,91,0.12)'};
    color: ${({ $priority }) =>
        $priority === 'high'   ? 'var(--clr-dark-red)'    :
        $priority === 'medium' ? 'var(--clr-dark-yellow)'  :
                                 'var(--clr-dark-green)'};
`

const Assignee = styled.span`
    font-size: var(--font-XXS);
    color: ${({ theme }) => theme.font.label};
    margin-left: auto;

    @media (max-width: ${({ theme }) => theme.breakpoint.l}) {
        display: none;
    }
`

const KanbanCard = ({ task, swimlaneId, parentId, parentEntity, parentPath, subEntity, cardConfig, testId }) => {
    const navigate = useNavigate()
    const { lsd } = useLocale()

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: task.id,
        data: { swimlaneId }
    })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    }

    const isOverdue = (() => {
        if (!task[cardConfig.dueDateField]) return false
        return new Date(task[cardConfig.dueDateField]) < new Date()
    })()

    const handleClick = () => {
        if (!isDragging) {
            navigate(`${parentPath}/${parentId}/${subEntity}/${task.id}`)
        }
    }

    return (
        <Card
            ref={setNodeRef}
            style={style}
            $isDragging={isDragging}
            onClick={handleClick}
            data-testid={testId}
            {...attributes}
            {...listeners}
        >
            <CardHeader>
                <CardTitle>{task[cardConfig.titleField]}</CardTitle>
                {task[cardConfig.dueDateField] && (
                    <DueDate $overdue={isOverdue}>
                        {lsd(task[cardConfig.dueDateField])}
                    </DueDate>
                )}
            </CardHeader>
            <CardMeta>
                {task[cardConfig.priorityField] && (
                    <PriorityBadge $priority={task[cardConfig.priorityField]}>
                        {task.priorityName || task[cardConfig.priorityField]}
                    </PriorityBadge>
                )}
                {task[cardConfig.assigneeField] && (
                    <Assignee>{task[cardConfig.assigneeField]}</Assignee>
                )}
            </CardMeta>
        </Card>
    )
}

export default KanbanCard
